import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

/**
 * Premium review carousel — 3 cards on desktop, 2 on tablet, 1 on mobile.
 * Slides 1 card at a time. Autoplay every 5.5s, pauses on hover. Swipe-enabled.
 */
export default function TestimonialCarousel({ items = [] }) {
  const [perView, setPerView] = useState(3);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);

  // Responsive perView
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w < 768) setPerView(1);
      else if (w < 1100) setPerView(2);
      else setPerView(3);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const maxIndex = Math.max(0, items.length - perView);

  // Clamp index when perView changes
  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [perView, maxIndex, index]);

  const next = useCallback(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);
  const prev = useCallback(() => setIndex((i) => (i <= 0 ? maxIndex : i - 1)), [maxIndex]);

  // Autoplay
  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [paused, next]);

  // Swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  if (!items.length) return null;

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonial-carousel"
    >
      <div className="overflow-hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div
          ref={trackRef}
          className="flex transition-transform duration-700 ease-[cubic-bezier(.22,.61,.36,1)]"
          style={{ transform: `translateX(-${(index * 100) / perView}%)` }}
        >
          {items.map((t, i) => (
            <div
              key={i}
              className="shrink-0 px-3"
              style={{ width: `${100 / perView}%` }}
              data-testid={`testimonial-slide-${i}`}
            >
              <article className="h-full rounded-[22px] p-8 md:p-9 bg-[#FAF6F1]/[0.06] border border-[#FAF6F1]/15 backdrop-blur-sm flex flex-col">
                <Quote size={28} className="text-[#C5A059]" />
                <p className="mt-5 text-[15.5px] md:text-[16.5px] text-[#FAF6F1]/92 leading-[1.7] font-display italic">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-7 pt-5 border-t border-[#FAF6F1]/12">
                  <p className="text-[11px] tracking-[0.24em] uppercase text-[#C5A059] font-semibold">{t.name}</p>
                  <p className="mt-1.5 text-[12.5px] text-[#FAF6F1]/65">{t.role}</p>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="mt-9 flex items-center justify-center gap-5">
        <button
          onClick={prev}
          aria-label="Previous testimonial"
          data-testid="testimonial-prev"
          className="w-11 h-11 rounded-full border border-[#FAF6F1]/30 text-[#FAF6F1]/85 hover:border-[#B65980] hover:text-[#FAF6F1] transition flex items-center justify-center"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex items-center gap-2" data-testid="testimonial-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[#C5A059]" : "w-3 bg-[#FAF6F1]/30 hover:bg-[#FAF6F1]/55"}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>

        <button
          onClick={next}
          aria-label="Next testimonial"
          data-testid="testimonial-next"
          className="w-11 h-11 rounded-full border border-[#FAF6F1]/30 text-[#FAF6F1]/85 hover:border-[#B65980] hover:text-[#FAF6F1] transition flex items-center justify-center"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
